import { ElementType } from "react";
import { Box, Chip, IconButton, Paper, Tooltip, Typography, useTheme } from "@mui/material";
import { DocumentSummary as SummaryData } from "@my-helpers/common";
import {
    Description as DocumentIcon,
    Visibility as ViewIcon,
    Delete as DeleteIcon,
} from '@mui/icons-material';

interface DocumentSummaryProps {
    document: SummaryData;
    handleViewDocument: (document: SummaryData) => void;
    handleDeleteClick: (document: SummaryData) => void;
}

const DocumentSummary: ElementType<DocumentSummaryProps> = (
    { document, handleViewDocument, handleDeleteClick }) => {

    const theme = useTheme();
    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatDate = (dateString: string): string => {
        if (!dateString) return 'Unknown';
        return new Date(dateString).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusColor = (status: string): string => {
        switch (status?.toLowerCase()) {
            case 'in progress':
                return theme.palette.info.light;
            case 'failed':
                return theme.palette.error.light;
            case 'succeeded':
                return theme.palette.primary.main;
            default:
                return 'default';
        }
    };

    return (
        <Paper
            key={document.id}
            sx={{
                width: '70%',
                flex: 1,
                p: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                '&:hover': {
                    backgroundColor: 'action.hover',
                }
            }}
        >
            <DocumentIcon sx={{ color: 'primary.main', fontSize: 32 }} />
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                    <Typography
                        variant="subtitle1"
                        sx={{
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}
                        title={document.file_name || 'Unknown'}
                    >
                        {document.file_name || 'Unknown'}
                    </Typography>
                    <Chip
                        label={document.process_state || 'Unknown'}
                        size="small"
                        variant={document.process_state === 'in progress' ? 'outlined' : 'filled'}
                        sx={{ 
                            ml: 1 , 
                            color: 'white',
                            backgroundColor: getStatusColor(document.process_state || '') 
                        }}
                    />
                    <Chip
                        label={document.status || 'Unknown'}
                        size="small"
                        color="default"
                        variant="outlined"
                        sx={{ ml: 1 }}
                    />
                </Box>
                <Typography variant="body2" color="text.secondary">
                    {document.mime_type?.split('/')[1]?.toUpperCase() || 'UNKNOWN'}
                    {document.file_size && ` • ${formatFileSize(document.file_size)}`}
                    {document.created_at && ` • ${formatDate(document.created_at)}`}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="View Document" arrow>
                    <IconButton
                        size="small"
                        onClick={() => handleViewDocument(document)}
                        sx={{
                            color: 'text.secondary',
                            '&:hover': {
                                color: 'primary.main'
                            }
                        }}
                    >
                        <ViewIcon fontSize="small"/>
                    </IconButton>
                </Tooltip>
                <Tooltip title="Delete Document">
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(document);
                        }}
                        sx={{
                            color: 'text.secondary',
                            '&:hover': {
                                color: 'error.main'
                            }
                        }}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
        </Paper>
    );
}

export default DocumentSummary;
